import styled from '@emotion/styled';
import React, { useCallback, useEffect, useState } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';
import { theme } from 'styles/theme/theme';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';
import { CropModalProps } from './types';

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.01;

const CENTER: Point = { x: 0, y: 0 };

/**
 * Пропорция кадра по умолчанию — квадрат.
 *
 * Не «свободная рамка»: `react-easy-crop` рамку не ресайзит вовсе, её размер
 * диктует пропорция, а кадр двигают зумом и перетаскиванием. Без явного
 * значения библиотека молча берёт собственный дефолт 4:3, поэтому «по
 * умолчанию» здесь всегда что-то конкретное — и это квадрат: он одинаково
 * ложится в квадратные плейсхолдеры логотипов и аватаров и не зависит от того,
 * снят ли исходник вертикально.
 */
const DEFAULT_ASPECT = 1;

/**
 * Ширина сцены и ползунка под ней — одна на двоих, иначе они разъезжаются.
 *
 * Ширину модалки задаёт `size="lg"` (760px), сцена в неё вписывается по `70vw`
 * и никогда не шире 640px. Без `margin: 0 auto` она прижималась бы к левому
 * краю окна, потому что окно шире сцены.
 */
const STAGE_WIDTH = 'min(70vw, 640px)';
const STAGE_WIDTH_MOBILE = 'min(100%, 640px)';

/** Сцена кропера обязана иметь ненулевой размер: без него библиотека не считает область. */
const CropperStage = styled.div`
  position: relative;
  width: ${STAGE_WIDTH};
  height: min(50vh, 420px);
  margin: 0 auto;
  background: ${theme.colors.SurfaceRaisinBlack50};
  border-radius: 8px;
  overflow: hidden;

  /*
   * Снимаем с картинки кропера ограничение Tailwind preflight: img { max-width: 100% }.
   *
   * В режиме cover библиотека задаёт картинке одну сторону (height: 100%,
   * вторая — auto) и ждёт, что вторая вырастет за края сцены. Глобальный
   * max-width: 100% эту вторую сторону обрезает по ширине сцены — и картинка
   * не выступает, а сплющивается в неё, теряя пропорции.
   */
  .reactEasyCrop_Cover_Horizontal,
  .reactEasyCrop_Cover_Vertical {
    max-width: none;
    max-height: none;
  }

  @media (max-width: 768px) {
    width: ${STAGE_WIDTH_MOBILE};
  }
`;

const ZoomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: ${STAGE_WIDTH};
  margin: 16px auto 0;

  input {
    flex: 1;
    accent-color: ${theme.colors.AccentNaplesYellow};
  }

  @media (max-width: 768px) {
    width: ${STAGE_WIDTH_MOBILE};
  }
`;

/**
 * Кадрирование выбранного изображения.
 *
 * Три исхода вместо двух: кроме «Обрезать» и «Отмены» есть «Оставить как есть».
 * Он нужен потому, что обрезка всегда перекодирует файл (см. `crop-image.ts`), а
 * человеку, который просто загружает готовую картинку, перекодировать нечего —
 * этот путь отдаёт исходный `File` со всеми его EXIF и прозрачностью.
 *
 * Модалка отдаёт только координаты области: саму вырезку рисует `cropImage`.
 */
export function CropModal({ file, aspect, onCrop, onSkip, onCancel }: CropModalProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>(CENTER);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [area, setArea] = useState<Area | null>(null);

  useEffect(() => {
    if (!file) {
      setImageUrl(null);
      return;
    }

    // Новый файл — новая сцена: и рамка, и зум с прошлого раза здесь не значат ничего.
    setCrop(CENTER);
    setZoom(MIN_ZOOM);
    setArea(null);

    const url = URL.createObjectURL(file);
    setImageUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const handleCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setArea(croppedAreaPixels);
  }, []);

  const handleCrop = useCallback(() => {
    if (!area) return;
    onCrop(area);
  }, [area, onCrop]);

  // Пока файла нет, компонента нет вовсе. Escape у соседней модалки скрытый
  // экземпляр больше не отнимает (это чинит стек в `ud-ui/components/modal`), но
  // держать смонтированным кропер без картинки всё равно незачем.
  if (!file) return null;

  return (
    <Modal show onClose={onCancel} noOverflow size="lg">
      {/*
        Подсказка короткая намеренно: на узком экране длинная фраза ломается на
        три строки и оставляет последнее слово висеть в одиночестве. Про
        ползунок здесь не пишем — он подписан «Масштаб» прямо под сценой.
      */}
      <Modal.Header title="Кадрирование" subtitle="Перетащите картинку, чтобы выбрать область" />
      <Modal.Body>
        <CropperStage>
          {imageUrl && (
            // `objectFit="cover"` — картинка подгоняется под рамку, а не наоборот.
            // При дефолтном `contain` рамку ограничивает вписанное изображение:
            // у панорамного снимка это его высота, и квадрат кадра съёживается до
            // узкой полоски посреди сцены, а зум двигает под ним картинку, вместо
            // того чтобы увеличивать саму рамку. С `cover` рамка всегда занимает
            // сцену целиком, а исходник заполняет её и выступает за края —
            // прицеливаться приходится картинкой, а не рамкой.
            //
            // `restrictPosition` при этом оставлен дефолтным (`true`): раз кадр
            // всегда покрыт картинкой, вытащить в него пустоту нельзя.
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect ?? DEFAULT_ASPECT}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              objectFit="cover"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          )}
        </CropperStage>
        <ZoomRow>
          <UDText variant="text-2">Масштаб</UDText>
          <input
            type="range"
            aria-label="Масштаб"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={ZOOM_STEP}
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
          />
        </ZoomRow>
      </Modal.Body>
      <Modal.Footer justify="between">
        <UDButton variant="secondary" type="button" onClick={onCancel}>
          <span>Отмена</span>
        </UDButton>
        <div className="flex items-center gap-3">
          <UDButton variant="secondary" type="button" onClick={onSkip}>
            <span>Оставить как есть</span>
          </UDButton>
          <UDButton variant="primary" type="button" disabled={!area} onClick={handleCrop}>
            <span>Обрезать</span>
          </UDButton>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default CropModal;
